import React, {
    useState, useEffect, useMemo, useCallback
} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {
    Button,
    ModalNext,
    ModalHeader,
    ModalBody,
    ModalFooter,
    useModal,
} from '@jutro/components';
import { useTranslator } from '@jutro/locale';
import { ViewModelForm, useDataRefresh } from 'gw-portals-viewmodel-react';
import { DocumentComponent } from 'gw-capability-document-react';
import { useValidation } from 'gw-portals-validation-react';
import { FileUtil } from 'gw-portals-util-js';
import { ServiceRequestService } from 'gw-capability-servicerequest';
import messages from './AddQuoteModal.messages';
import metadata from './AddQuoteModal.metadata.json5';

function AddQuoteModal(props) {
    const {
        showAlert
    } = useModal();

    const [quoteVM, setQuoteVM] = useState({});
    const translator = useTranslator();
    const [isLoading, setLoadingState] = useState(false);
    const { refreshData } = useDataRefresh();
    const { isComponentValid, onValidate, registerComponentValidation } = useValidation('AddQuoteModal');
    const {
        isOpen,
        onReject,
        onResolve,
        quoteValue,
        onAfterClose,
        serviceRequestID,
        existingDocuments,
        viewModelService,
        authHeader,
    } = props;

    const quoteAlreadyExists = useMemo(() => !_.isEmpty(quoteValue), [quoteValue]);

    useEffect(() => {
        if (quoteAlreadyExists) {
            quoteValue.existingDocumentsToLink = quoteValue.documents.map(
                (document) => document.publicID
            );
        }
        setQuoteVM(
            viewModelService.create(
                quoteValue,
                'cc',
                'edge.capabilities.servicerequest.statement.quote.dto.ServiceRequestQuoteDTO'
            )
        );
        // only execute once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const validateDocuments = useCallback(() => {
        const documentsArray = _.get(quoteVM, 'documents.value', []);
        return documentsArray.length !== 0;
    }, [quoteVM]);

    useEffect(() => registerComponentValidation(validateDocuments), [
        registerComponentValidation, validateDocuments
    ]);

    const handleAddQuote = useCallback(() => {
        onResolve(quoteVM);
    }, [quoteVM, onResolve]);

    const handleClose = useCallback(() => {
        onReject();
    }, [onReject]);

    const handleQuoteVMChange = useCallback((newModel) => {
        setQuoteVM(newModel);
        refreshData();
    }, [refreshData]);

    const handleUploadDocument = useCallback(async (file) => {
        setLoadingState(true);
        const documentsArray = _.get(quoteVM, 'documents.value', []);
        const documentMetadataTemplate = {
            serviceRequestId: serviceRequestID,
            name: FileUtil.checkFileName(file[0].name, [...existingDocuments, ...documentsArray]),
            mimeType: file[0].type,
            sessionID: await ServiceRequestService.generateUploadToken(authHeader),
            documentType: 'AX_quotation'
        };
        ServiceRequestService.uploadDocument(file[0], documentMetadataTemplate, authHeader)
            .then((data) => {
                const existingDocumentsToLink = _.get(quoteVM, 'existingDocumentsToLink.value', []);
                _.set(quoteVM, 'documents.value', [...documentsArray, data]);
                _.set(quoteVM, 'existingDocumentsToLink.value', [...existingDocumentsToLink, data.publicID]);
                setQuoteVM(quoteVM);
                refreshData();
            }).catch(() => {
                showAlert({
                    title: messages.documentUploadFailed,
                    message: messages.documentCannotBeUploaded,
                    status: 'error',
                    icon: 'mi-error-outline'
                });
            }).finally(() => {
                setLoadingState(false);
            });
    }, [authHeader, existingDocuments, quoteVM, refreshData, serviceRequestID]);

    const handleDeleteDocument = useCallback(
        async (ccPublicID) => {
            setLoadingState(true);
            const documentsArray = _.get(quoteVM, 'documents.value', []);
            const serviceRequestId = _.get(documentsArray.find(({ publicID }) => publicID === ccPublicID), 'serviceRequestId');
            const isDeleteItem = await ServiceRequestService.removeDocument(
                serviceRequestId,
                ccPublicID,
                authHeader
            );

            if (isDeleteItem) {
                // eslint-disable-next-line max-len
                const updatedArray = documentsArray.filter(({ publicID }) => publicID !== ccPublicID);
                _.set(quoteVM, 'documents.value', updatedArray);
                setQuoteVM(quoteVM);
                refreshData();
            }
            setLoadingState(false);
        },
        [authHeader, quoteVM, refreshData]
    );

    if (_.isEmpty(quoteVM)) {
        return null;
    }

    const overrides = {
        '@field': {
            showOptional: true
        },
        fileUpload: {
            deleteDocument: handleDeleteDocument,
            uploadDocument: handleUploadDocument,
            downloadDocument: ServiceRequestService.downloadDocumentLink,
            isLoading: isLoading
        }
    };

    const resolvers = {
        resolveComponentMap: {
            documentcomponent: DocumentComponent,
        }
    };

    return (
        <ModalNext isOpen={isOpen} onRequestClose={handleClose} onAfterClose={onAfterClose}>
            <ModalHeader title={quoteAlreadyExists
                ? translator(messages.reviseQuote) : translator(messages.addQuote)}
            />
            <ModalBody>
                <ViewModelForm
                    uiProps={metadata.componentContent.content}
                    model={quoteVM}
                    onValidationChange={onValidate}
                    onModelChange={handleQuoteVMChange}
                    componentMap={resolvers.resolveComponentMap}
                    overrideProps={overrides}
                />
            </ModalBody>
            <ModalFooter>
                <Button
                    type="tertiary"
                    onClick={handleClose}
                >
                    {translator(messages.cancel)}
                </Button>
                <Button
                    onClick={handleAddQuote}
                    disabled={!isComponentValid}
                >
                    {translator(messages.save)}
                </Button>
            </ModalFooter>
        </ModalNext>
    );
}

AddQuoteModal.propTypes = {
    quoteValue: PropTypes.shape({
        existingDocumentsToLink: PropTypes.arrayOf({}),
        documents: PropTypes.arrayOf({}),
    }),
    existingDocuments: PropTypes.arrayOf({}),
    serviceRequestID: PropTypes.string,
    isOpen: PropTypes.bool,
    onResolve: PropTypes.func,
    onReject: PropTypes.func,
    onAfterClose: PropTypes.func,
};

AddQuoteModal.defaultProps = {
    onAfterClose: _.noop,
    isOpen: false,
    onResolve: _.noop,
    onReject: _.noop,
    quoteValue: {},
    existingDocuments: [],
    serviceRequestID: undefined
};

export default AddQuoteModal;
